import { PrimaryDataSectionProps } from "./PrimaryDataSection.types"
import SectionDataHeatmap from "../SectionDataHeatmap/SectionDataHeatmap"
import AnalysisSection from "../AnalysisSection/AnalysisSection"
import ScanDataGraph from "../ScanDataGraph/ScanDataGraph"
import THzHeatmapRow from "../../../../components/THzHeatmapRow/THzHeatmapRow"
import VoiceNoteSection from "../VoiceNoteSection/VoiceNoteSection"
import CommentsSection from "../CommentsSection/CommentsSection"
import SkeletonLoadingView from "../../../../components/SkeletonLoadingView/SkeletonLoadingView"
import { useRef, useState } from "react"
import EllipsisMenu from "../../../../components/EllipsisMenu/EllipsisMenu"
import DownIcon from "../../../../media/DownIcon"
import ErrorView from "../SectionError/ErrorView"
import { AngularIsolatedScan, IsolatedScan, PatchIsolatedScan } from "../../../../types/starAPIResponseTypes"
import WorkflowType from "../../../../enums/workflowType"
import SectionDataHeatmapVertical from "../SectionDataHeatmap/SectionDataHeatmapVertical"
import HeatmapSection from "../HeatmapSection/HeatmapSection"
import "./PrimaryDataSection.css"

const PrimaryDataSection = (props: PrimaryDataSectionProps) => {

  const {
    title,
    sectionHeatmap,
    graphDataKeys,
    graphDataMapper,
    voiceNote,
    scanComment,
    processedSection,
    minHeatmapKey,
    maxHeatmapKey,
    showAssetBaseline,
    verticalHeatmap
  } = props

  const [showMenu, setShowMenu] = useState<boolean>(false)
  const heatmapKeyRef = useRef<HTMLCanvasElement>(null)
  const heatmapRef = useRef<HTMLCanvasElement>(null)
  const graphHeatmapRef = useRef<HTMLCanvasElement>(null)

  const heatmapData = sectionHeatmap.heatmaps
  const heatmapMaskData = sectionHeatmap.heatmapMask
  const scanMetadata = sectionHeatmap.isolatedScans
  const processedSectionData = processedSection

  const SKELETON_HEATMAP_SCAN_HEIGHT = "2em"
  const SKELETON_GRAPH_HEIGHT = "15em"

  const handleDownload = () => {
    if (!heatmapRef.current) return
    
    heatmapRef.current.toBlob((blob) => {
      if (!blob) return
      const date = new Date()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${title}-${date.toISOString()}.png`
      link.click()
      URL.revokeObjectURL(url)
    })
  }

  const getScanPositionMetadata = (isolatedScans: IsolatedScan[]) => {
    if (isolatedScans.length === 0) { return [] }
    switch (isolatedScans[0].isolated_scan_type) {
        case WorkflowType.ANGULAR:
            const angularIsolatedScans = isolatedScans as AngularIsolatedScan[]
            return angularIsolatedScans.map((isolatedScan) => `Scan: ${isolatedScan.scan_no+1} - ${(isolatedScan.angle)}°`)
        case WorkflowType.PATCH:
            const patchIsolatedScans = isolatedScans as PatchIsolatedScan[]
            return patchIsolatedScans.map((isolatedScan) => `Scan ${isolatedScan.scan_no+1} - ${isolatedScan.horizontal_position}mm`)
        default: return isolatedScans.map((isolatedScan) => `Scan: ${isolatedScan.scan_no+1}`)
    }
  }

  if (!heatmapData || !scanMetadata || !processedSectionData) return null

  if (
    heatmapData.status === "loading" || heatmapData.status === "idle"
  ) {
    return (
      <AnalysisSection title={title}>
        <>
          <div className="analysis-section thz-section-loading-container">
            {[...Array(8)].map(() => (
              <SkeletonLoadingView style={{height: SKELETON_HEATMAP_SCAN_HEIGHT}}/>
            ))}
          </div>

          <div className="analysis-section thz-section-loading-container">
            <SkeletonLoadingView style={{height: SKELETON_GRAPH_HEIGHT}} />
          </div>
        </>
      </AnalysisSection>
    )
  }

  if (processedSectionData.status === "error") {
    return (
      <AnalysisSection title={title}>
        <ErrorView 
          message={processedSectionData.error.message}
        />
      </AnalysisSection>
    )
  }

  if (
    heatmapMaskData.status === "loading" || heatmapMaskData.status === "idle" ||
    scanMetadata.status === "loading" || scanMetadata.status === "idle" ||
    processedSectionData.status === "loading" || processedSectionData.status === "idle" ||
    sectionHeatmap.selectedScan === -1
  ) {
    return (
      <AnalysisSection title={title}>
        <>
          <div className="analysis-section thz-section-loading-container">
            {[...Array(8)].map(() => (
              <SkeletonLoadingView style={{height: SKELETON_HEATMAP_SCAN_HEIGHT}}/>
            ))}
          </div>

          <div className="analysis-section thz-section-loading-container">
            <SkeletonLoadingView style={{height: SKELETON_GRAPH_HEIGHT}} />
          </div>
        </>
      </AnalysisSection>
    )
  }

  if (heatmapData.status === "error" || scanMetadata.status === "error" || heatmapMaskData.status === "error") {
    return (
      <AnalysisSection title={title}>
        <ErrorView 
          message="Failed to load heatmap. Please try again."
        />
      </AnalysisSection>
    )
  }

  const heatmaps = heatmapData.data
  const metadata = scanMetadata.data
  const heatmapMask = heatmapMaskData.data
  const processedSectionMetadata = processedSectionData.data

  if (!heatmaps || !metadata || !processedSectionMetadata) {
    return (
      <AnalysisSection title={title}>
        <ErrorView 
          message="Failed to load heatmap. Please try again."
        />
      </AnalysisSection>
    )
  }

  return (
    <AnalysisSection
      title={title}
      titleChild={
        <EllipsisMenu 
          ellipsisMenu={{
            items: [{
              item: "Download Heatmap",
              onClick: () => handleDownload()
            }],
            showMenu: showMenu,
            onClick: () => setShowMenu(true),
            onHide: () => setShowMenu(false),
          }}
        />
      }
    >
    <HeatmapSection
      vertical={verticalHeatmap}
        heatmap={{
          ...sectionHeatmap,
          heatmaps: heatmaps,
          overlayBoxes: sectionHeatmap.showFeatureVectors ? processedSectionMetadata.feature_vectors : undefined,
          skippedIndexes: metadata.map((_, index) => index).filter(index => metadata[index]?.calibration_start_p1 === 0),
          heatmapMask: heatmapMask,
          scanPositionMetadata: getScanPositionMetadata(metadata),
          canvasRef: heatmapRef,
        }}
        backgroundValue={processedSectionMetadata.background_value}
        maxValue={processedSectionMetadata[maxHeatmapKey] as number}
        minValue={processedSectionMetadata[minHeatmapKey] as number}
        offsetStart={metadata[sectionHeatmap.selectedScan].offset_start ?? 0}
        offsetEnd={metadata[sectionHeatmap.selectedScan].offset_end ?? 0}
        showAssetBaseline={showAssetBaseline}
      />

      <div className="analysis-section">
        <ScanDataGraph
          dataKeys={graphDataKeys}
          dataMapper={graphDataMapper}
          width={sectionHeatmap.width}
          scanData={heatmaps[verticalHeatmap ? heatmaps.length-1-sectionHeatmap.selectedScan : sectionHeatmap.selectedScan]}
          invertScanData={verticalHeatmap}
          min={processedSectionMetadata[minHeatmapKey] as number}
          max={processedSectionMetadata[maxHeatmapKey] as number}
        />
        <SectionDataHeatmap
          {...sectionHeatmap}
          heatmapMask={heatmapMask}
          heatmaps={[heatmaps[verticalHeatmap ? heatmaps.length-1-sectionHeatmap.selectedScan :sectionHeatmap.selectedScan]]}
          scanPositionMetadata={[]}
          interactionEnabled={false}
          canvasRef={graphHeatmapRef}
        />
      </div>

      <VoiceNoteSection 
        {...voiceNote}
        scanId={metadata[sectionHeatmap.selectedScan].isolated_scan_id}
        voiceNoteSrc={metadata[sectionHeatmap.selectedScan].voice_note_src}
      />
      
      {scanComment && (
        <CommentsSection 
          {...scanComment}
        />
      )}
    </AnalysisSection>
  )
}

export default PrimaryDataSection